import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { listLoginEvents, getLoginEventsSummary } from '../../services/api';
import { toast } from 'sonner';
import { RefreshCcw, KeyRound, LogIn, ShieldCheck, UserCheck, Users2, Search, Loader2, ArrowUpRight } from 'lucide-react';

/**
 * /admin/login-activity — admin-only audit log of portal sign-ins.
 *
 * Focus is squarely on the question "which customers have actually
 * signed in and when?" — everything else (invite redemptions, password
 * resets, admin impersonations) is exposed via a filter so the same
 * page doubles as a security-review tool.
 *
 * Pagination is intentionally not implemented — the backend clamps to
 * 2k rows and returns a `truncated` flag. If a customer estate ever
 * grows past that we'll switch to a cursor-based load-more; until then
 * the "range" filter is the natural refinement path.
 */

const EVENT_META = {
  login:            { label: 'Signed in',      icon: LogIn,      className: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
  'invite-redeem':  { label: 'Accepted invite', icon: UserCheck,  className: 'text-sky-700 bg-sky-50 border-sky-100' },
  'reset-redeem':   { label: 'Reset password',  icon: KeyRound,   className: 'text-amber-700 bg-amber-50 border-amber-100' },
  impersonate:      { label: 'Admin viewed',    icon: ShieldCheck,className: 'text-slate-700 bg-slate-50 border-slate-200' },
};

const ROLE_META = {
  customer: 'text-emerald-700 bg-emerald-50',
  admin:    'text-slate-700 bg-slate-100',
  employee: 'text-sky-700 bg-sky-50',
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// Best-effort browser sniff from the UA — good enough to render a
// "Chrome on macOS" hint without shipping a full parser.
const summariseUA = (ua = '') => {
  if (!ua) return '—';
  const s = ua.toLowerCase();
  let browser = 'Browser';
  if (s.includes('firefox')) browser = 'Firefox';
  else if (s.includes('edg/')) browser = 'Edge';
  else if (s.includes('chrome')) browser = 'Chrome';
  else if (s.includes('safari')) browser = 'Safari';
  else if (s.includes('curl')) browser = 'API/curl';

  let os = '';
  if (s.includes('iphone') || s.includes('ipad')) os = 'iOS';
  else if (s.includes('android')) os = 'Android';
  else if (s.includes('mac os')) os = 'macOS';
  else if (s.includes('windows')) os = 'Windows';
  else if (s.includes('linux')) os = 'Linux';
  return os ? `${browser} on ${os}` : browser;
};

const LoginActivity = () => {
  const [role, setRole] = useState('customer');   // most common admin question
  const [event, setEvent] = useState('all');
  const [days, setDays] = useState(30);
  const [q, setQ] = useState('');
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { days, limit: 500 };
      if (role !== 'all') params.role = role;
      if (event !== 'all') params.event = event;
      const [{ events: rows, truncated: t }, s] = await Promise.all([
        listLoginEvents(params),
        getLoginEventsSummary(days),
      ]);
      setEvents(rows);
      setTruncated(t);
      setSummary(s);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load login activity');
    } finally {
      setLoading(false);
    }
  }, [role, event, days]);

  useEffect(() => { load(); }, [load]);

  // Client-side "search" — email or name substring. The dataset is
  // already capped server-side at 500 rows so scanning here is cheap
  // and keeps the network round-trip free of unindexed regex queries.
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return events;
    return events.filter((e) => {
      const hay = `${e.email || ''} ${e.name || ''} ${e.company || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [events, q]);

  return (
    <div className="space-y-4" data-testid="login-activity-page">
      <PageHeader
        title="Portal Login Activity"
        subtitle="See who's been signing into the portal and when. Only admins can view this page."
        actions={
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="login-activity-refresh">
            <RefreshCcw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {/* Summary strip — read-only quick counters. */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label="Customer sign-ins"
          hint={`in the last ${days} days`}
          value={summary?.customerLogins ?? '—'}
          icon={LogIn}
          testId="stat-customer-logins"
        />
        <Stat
          label="Unique customers"
          hint={`signed in over ${days}d`}
          value={summary?.uniqueCustomers ?? '—'}
          icon={Users2}
          testId="stat-unique-customers"
        />
        <Stat
          label="Invites accepted"
          hint={`in the last ${days} days`}
          value={summary?.inviteRedemptions ?? '—'}
          icon={UserCheck}
          testId="stat-invites"
        />
        <Stat
          label="Password resets"
          hint={`in the last ${days} days`}
          value={summary?.resetRedemptions ?? '—'}
          icon={KeyRound}
          testId="stat-resets"
        />
      </div>

      {/* Filter bar */}
      <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Search</div>
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Email, name, or company…"
              className="pl-7 h-9"
              data-testid="login-activity-search"
            />
          </div>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Role</div>
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-9 w-[140px]" data-testid="login-activity-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">Customer</SelectItem>
              <SelectItem value="employee">Employee</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
              <SelectItem value="all">All roles</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Event</div>
          <Select value={event} onValueChange={setEvent}>
            <SelectTrigger className="h-9 w-[170px]" data-testid="login-activity-event"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All events</SelectItem>
              <SelectItem value="login">Sign-ins</SelectItem>
              <SelectItem value="invite-redeem">Invite accepted</SelectItem>
              <SelectItem value="reset-redeem">Password reset</SelectItem>
              <SelectItem value="impersonate">Admin viewed</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1">Range</div>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="h-9 w-[130px]" data-testid="login-activity-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Log table */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="login-activity-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">User</th>
                <th className="text-left px-3 py-2">Event</th>
                <th className="text-left px-3 py-2">Where</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={4} className="px-3 py-10 text-center text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={4} className="px-3 py-10 text-center text-slate-400" data-testid="login-activity-empty">
                  Nothing yet — no {role === 'all' ? '' : role} events in the last {days} days.
                </td></tr>
              )}
              {!loading && filtered.map((e, i) => {
                const meta = EVENT_META[e.event] || { label: e.event, icon: LogIn, className: 'text-slate-700 bg-slate-50' };
                const Icon = meta.icon;
                return (
                  <tr key={`${e.timestamp}-${e.userId}-${i}`} className="hover:bg-slate-50" data-testid={`login-event-row-${i}`}>
                    <td className="px-3 py-2 whitespace-nowrap align-top">
                      <div className="text-slate-900">{fmtWhen(e.timestamp)}</div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-slate-900 truncate max-w-[240px]" title={e.name}>{e.name || '—'}</div>
                          <div className="text-xs text-slate-500 truncate max-w-[240px]" title={e.email}>
                            {e.userId ? (
                              <Link
                                to={e.role === 'customer' ? `/admin/customers/${e.contactOfCustomerId || e.parentCustomerId || e.userId}` : `/admin/users/${e.userId}`}
                                className="hover:text-slate-900 hover:underline"
                              >
                                {e.email}
                                <ArrowUpRight className="h-3 w-3 inline ml-0.5 -mt-0.5" />
                              </Link>
                            ) : (e.email || '—')}
                          </div>
                        </div>
                        {e.role && (
                          <span className={`ml-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded ${ROLE_META[e.role] || 'bg-slate-100 text-slate-600'}`}>{e.role}</span>
                        )}
                      </div>
                      {e.company && <div className="text-xs text-slate-500 mt-0.5">{e.company}</div>}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded border ${meta.className}`}>
                        <Icon className="h-3 w-3" />
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <div className="text-slate-700 font-mono text-xs">{e.ip || '—'}</div>
                      <div className="text-xs text-slate-500 mt-0.5" title={e.userAgent}>{summariseUA(e.userAgent)}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {truncated && (
          <div className="text-xs text-amber-800 bg-amber-50 border-t border-amber-100 px-3 py-2" data-testid="login-activity-truncated">
            Showing the most recent 500 entries — narrow the range or apply a filter to see older activity.
          </div>
        )}
      </div>
    </div>
  );
};

const Stat = ({ label, hint, value, icon: Icon, testId }) => (
  <div className="bg-white border border-slate-200 rounded-md px-3 py-3 flex items-start gap-3" data-testid={testId}>
    <div className="mt-1 h-8 w-8 rounded bg-slate-900 text-white flex items-center justify-center">
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="text-2xl font-display tracking-tight text-slate-900 leading-tight">{value}</div>
      <div className="text-[11px] text-slate-400">{hint}</div>
    </div>
  </div>
);

export default LoginActivity;
